import { jsonify, sanitize } from "../TapiocaUtils";
export default class PaddClient {
  constructor(raw) {
    this.raw = raw;
  }
  /**
   * Activates the deactivated automations between `source_project_uid` and
   * `target_project_uid`.  Automations can be deactivated on the source and/or target
   * side, if an automation is deactivated (either on source or target side) events that
   * would match it would be ignored (as if it didn't exist).  And so, depending on the
   * following non-exclusive conditions an automation can be "source and/or target
   * re-activated":
   * * If the requesting user is an admin on the source project or if it is the user that
   * created the automation in the first place, then it will be "source activated".
   * * If the requesting user is an admin on the target project, then it will be "target
   * activated".
   * Note: this endpoint will collect all the sender_user_oids of the automations from the
   * given source to target project uid, and it will source-reactivate them if the user
   * was the creator of **all** of them.
   * If you request to activate an automation that's already active, nothing will happen.
   */
  activateAutomations(sourceProjectUid, targetProjectUid) {
    const url = `/v1/automations/${sourceProjectUid}/${targetProjectUid}/activate`;
    return this.raw.patch(url, {});
  }
  /**
   * Activates the automations with the uids passed by parameter.
   * Automations can be deactivated on the source and/or target side, if an automation is
   * deactivated (either on source or target side) events that would match it would be
   * ignored (as if it didn't exist).  And so, depending on the following non-exclusive
   * conditions an automation can be "source and/or target re-activated":
   * * If the requesting user is an admin on the source project or if it is the user that
   * created the automation in the first place, then it will be "source activated".
   * * If the requesting user is an admin on the target project, then it will be "target
   * activated".
   * If you request to activate an automation that's already active, nothing will happen.
   * Note that this endpoint will collect the source and target projects of all the given
   * automations and then only source/target-activate them if the user is admin of all the
   * source or all the target projects, (or source-activate them if the user is the
   * requesting user is the sender_user_oid of all the automations).
   * In case of doubt, call this endpoint on a set of automations sharing the same
   * (source_project_uid, target_project_uid) pair.
   */
  activateAutomationsBatch(body) {
    const url = "/v1/automations/activate:batch";
    return this.raw.patch(url, jsonify(body));
  }
  addMoreAssetsWorkspaceMigration(cloneUid, body) {
    const url = `/v1/workspace_migration/${cloneUid}`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Handles update of clone_asset_status + subscription asset status
   */
  assetStatusUpdate(assetStatusUid, body) {
    const url = `/v1/asset_status_update/${assetStatusUid}`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Soft-Delete the Automations identified by the uids passed by parameter.
   * Deleting an automation does not result in the deletion of any asset, its effect is
   * equivalent to the automations themselves not existing, that is deleted automations
   * will not get triggered when a matching even happens.
   * In order to delete an automation, the user has to be an admin on at least the source
   * or target projects.
   */
  batchDelete(body) {
    const url = "/v1/automations:batch-delete";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Creates subscriptions for multiple objects
   * Optionally emits upsert events on created subscriptions (and existing subscriptions)
   * Returns:
   * 200: { "created_subscriptions": [subscriptions] }
   */
  batchSubscriptionCreate(body) {
    const url = "/v1/subscriptions_/batch_create";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Deletes subscriptions for multiple objects
   * Optionally emits upsert events on deleted subscriptions (and already deleted subscriptions)
   * Returns:
   * 200: { "deleted_subscriptions": [subscriptions], "non_existent_subscriptions": [subscription_request_objects] }
   */
  batchSubscriptionDelete(body) {
    const url = "/v1/subscriptions_/batch_delete";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * DEPRECATED:
   * This endpoint is being deprecated in favor of `platform_create_clone`:
   * `POST duplication/v1/clones`
   * Support for this endpoint will be removed after Nov 1, 2022.
   */
  cloneCreate(body) {
    const url = "/v1/experimental/clones";
    return this.raw.post(url, jsonify(body));
  }
  cloneDelete(creationToken) {
    const url = `/v1/clones/${creationToken}`;
    return this.raw.delete(url);
  }
  /**
   * DEPRECATED:
   * This endpoint is being deprecated in favor of `platform_clone_fetch`:
   * `GET duplication/v1/clones/<uuid_string:clone_id>`
   * Support for this endpoint will be removed after Nov 1, 2022.
   */
  cloneFetch(cloneUid) {
    const url = `/v1/experimental/clones/${cloneUid}`;
    return this.raw.get(url);
  }
  /**
   * This endpoint performs cleanup related to a completed file package
   */
  completeFilePackage(body) {
    const url = "/v1/file_package";
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * This endpoint creates a bridge based on an existing bridge request.
   */
  createBridge(body) {
    const url = "/v1/bridges";
    return this.raw.post(url, jsonify(body));
  }
  createStaples(transmittalUid) {
    const url = `/v1/transmittals/${transmittalUid}/staples`;
    return this.raw.put(url, {});
  }
  createWorkspaceMigration(body) {
    const url = "/v1/workspace_migration";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Deactivates the automations from the given source to target project uids.
   * If the requesting user is the sender_user_oid of all the automations from the given
   * source to target project, or if the user is an admin of the source and/or target
   * projects, then the all the automations from the source to the target project passed
   * by parameter will be source and target-deactivated.
   */
  deactivateAutomations(sourceProjectUid, targetProjectUid) {
    const url = `/v1/automations/${sourceProjectUid}/${targetProjectUid}/deactivate`;
    return this.raw.patch(url, {});
  }
  /**
   * This endpoint deletes an active bridge.
   */
  deleteBridge(bridgeId, params) {
    const url = {
      url: `/v1/bridges/${bridgeId}`,
      params: sanitize(params || {}),
    };
    return this.raw.delete(url);
  }
  /**
   * Given `source_project_uid` and `target` (project uid), this endpoint soft-deletes the
   * automations from the specified source to the target projects.  A comma separated list
   * of `asset_types` can be specified if we want to delete only the automations with those
   * asset types.
   * This endpoint does not yet support automations to users, so target has to be a project
   * uid.
   * User has to be an admin of at least the source or target projects specified.
   */
  deleteProjectAutomations(sourceProjectUid, target, params) {
    const url = {
      url: `/v1/automations/${sourceProjectUid}/${target}`,
      params: sanitize(params || {}, { asset_types: "csv" }),
    };
    return this.raw.delete(url);
  }
  editTransmittalAssets(transmittalUid, body, params) {
    const url = {
      url: `/v1/transmittals/${transmittalUid}/assets`,
      params: sanitize(params || {}),
    };
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * This endpoint records the assets requested by the current user
   * and returns an identifier for the record.
   */
  filePackageAccept(body) {
    const url = "/v1/transaction";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint records the assets requested by the current user into
   * an existing record.
   */
  filePackageAcceptAddAssets(transactionId, body) {
    const url = `/v1/transaction/${transactionId}`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint will add assets to an existing file package.
   */
  filePackageAddAssets(filePackageId, body) {
    const url = `/v1/file-package/${filePackageId}`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint will create a file package and return its identifier.
   */
  filePackageCreate(body) {
    const url = "/v1/file-package";
    return this.raw.post(url, jsonify(body));
  }
  filterSubscriptionsByAssetStatus(body) {
    const url = "/v1/subscriptions_/asset_status_filter";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint fetches information about a bridge request.  There is no authorization
   * performed here, as we need to access the Bridge Request in the context of
   */
  getBridgeRequest(bridgeRequestId) {
    const url = `/v1/bridge-requests/${bridgeRequestId}`;
    return this.raw.get(url);
  }
  /**
   * Gets a list of neigboring projects for the source project of a bridge request
   */
  getBridgeRequestNeighbors(bridgeRequestId, params) {
    const url = {
      url: `/v1/bridge-requests/${bridgeRequestId}/sender-neighbors`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint fetches bridge requests for a given project.
   */
  getBridgeRequests(params) {
    const url = {
      url: "/v1/bridge-requests",
      params: sanitize(params || {}, { "filter[status]": "csv" }),
    };
    return this.raw.get(url);
  }
  /**
   * Despite this end point being labeled as 'clones', it currently is specifically
   * tied to 'workspaces', a specialization of 'clones'.
   */
  getClones(params) {
    const url = {
      url: "/v1/clones",
      params: sanitize(params || {}, { status: "multi" }),
    };
    return this.raw.get(url);
  }
  getPendingUsers(templateId, params) {
    const url = {
      url: `/v1/templates/${templateId}/users`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint fetches bridges for a given project.
   */
  getProjectBridges(params) {
    const url = {
      url: "/v1/bridges",
      params: sanitize(params || {}, { "filter[status]": "csv" }),
    };
    return this.raw.get(url);
  }
  getReceivedTransaction(transactionId) {
    const url = `/v1/transaction/received_transaction/${transactionId}`;
    return this.raw.get(url);
  }
  getReceivedTransactionAssets(transactionId, params) {
    const url = {
      url: `/v1/received_transaction/${transactionId}/assets`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint records the assets requested by the current user into
   * an existing record.
   */
  getReceivedTransactions(projectId, params) {
    const url = {
      url: `/v1/project/${projectId}/received_transactions`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint fetches a single sent transaction based on its unique identifier.
   */
  getSentTransaction(transactionId) {
    const url = `/v1/transaction/sent_transaction/${transactionId}`;
    return this.raw.get(url);
  }
  /**
   * This endpoint records the assets requested by the current user into
   * an existing record.
   */
  getSentTransactions(projectId, params) {
    const url = {
      url: `/v1/project/${projectId}/sent_transactions`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  getSentTransactionsAssets(transactionId, params) {
    const url = {
      url: `/v1/sent_transaction/${transactionId}/assets`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  getSubscription(targetProjectUid, targetObjectUid) {
    const url = `/v1/subscriptions_/target_project/${targetProjectUid}/target_object/${targetObjectUid}`;
    return this.raw.get(url);
  }
  getSubscriptions(targetProjectUid, params) {
    const url = {
      url: `/v1/subscriptions_/target_project/${targetProjectUid}`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Get a template at the account level.
   */
  getTemplate(templateId) {
    const url = `/v1/templates/${templateId}`;
    return this.raw.get(url);
  }
  getTransmittal(transmittalUid) {
    const url = `/v1/transmittals/${transmittalUid}`;
    return this.raw.get(url);
  }
  getTransmittalAssets(transmittalUid, params) {
    const url = {
      url: `/v1/transmittals/${transmittalUid}/assets`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  getTransmittals(params) {
    const url = {
      url: "/v1/transmittals",
      params: sanitize(params || {}, { status: "csv" }),
    };
    return this.raw.get(url);
  }
  /**
   * Obtain a paginated list of templates + published status in a given account.
   */
  listAccountTemplates(accountId, params) {
    const url = {
      url: `/v1/templates/account/${accountId}/templates`,
      params: sanitize(params || {}, {
        "filter[isPublished]": "csv",
        "filter[templateId]": "csv",
      }),
    };
    return this.raw.get(url);
  }
  pendingUserDelete(templateId, pendingUserUid) {
    const url = `/v1/templates/${templateId}/users/${pendingUserUid}`;
    return this.raw.delete(url);
  }
  pendingUserEdit(templateId, pendingUserUid, body) {
    const url = `/v1/templates/${templateId}/users/${pendingUserUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  pendingUsersCreate(templateId, body) {
    const url = `/v1/templates/${templateId}/users`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Fetch detailed metadata for a clone's asset status matching the provided filters.
   */
  platformCloneAssetStatusFilter(cloneId, params) {
    const url = {
      url: `/v1/duplication/v1/clones/${cloneId}/asset-status`,
      params: sanitize(params || {}, { "filter[status]": "csv" }),
    };
    return this.raw.get(url);
  }
  /**
   * NOTE: !!!!! THIS ENDPOINT IS NOT IMPLEMENTED !!!!!
   * Please contact #acs-lifecycle-project-eng if you have a use case for this...
   * PJL-3528: [PADD] Implement clone collection filter endpoint
   * Fetch detailed metadata for clones matching the provided filters.
   */
  platformCloneCollectionFilter(params) {
    const url = {
      url: "/v1/duplication/v1/clones",
      params: sanitize(params || {}, {
        "filter[id]": "csv",
        "filter[sourceProjectId]": "csv",
        "filter[targetProjectId]": "csv",
      }),
    };
    return this.raw.get(url);
  }
  /**
   * Fetch detailed metadata for a given clone.
   * Ideally, the clone_id parameter should have been named 'cloneId' to comply with our external
   * path arg naming conventions, but at this point it would be risky to change it as it would
   * impact the swagger file and any generated clients
   */
  platformCloneFetch(cloneId) {
    const url = `/v1/duplication/v1/clones/${cloneId}`;
    return this.raw.get(url);
  }
  /**
   * NOTE: !!!!! THIS ENDPOINT IS NOT IMPLEMENTED !!!!!
   * Please contact #acs-lifecycle-project-eng if you have a use case for this...
   * PJL-3529: [PADD] Implement clone progress collection filter
   * Fetch detailed clone progress information based on the provided filters.
   */
  platformCloneProgressCollectionFilter(params) {
    const url = {
      url: "/v1/duplication/v1/clone-progress",
      params: sanitize(params || {}, { "filter[id]": "csv" }),
    };
    return this.raw.get(url);
  }
  /**
   * Fetch clone progress details for a given clone.
   * Ideally, the clone_id parameter should have been named 'cloneId' to comply with our external
   * path arg naming conventions, but at this point it would be risky to change it as it would
   * impact the swagger file and any generated clients
   */
  platformCloneProgressFetch(cloneId) {
    const url = `/v1/duplication/v1/clones/${cloneId}/progress`;
    return this.raw.get(url);
  }
  /**
   * Create a clone with asset subscriptions.
   */
  platformCreateClone(body) {
    const url = "/v1/duplication/v1/clones";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a clone with asset subscriptions based on asset type.
   */
  platformCreateCloneByAssetType(body) {
    const url = "/v1/duplication/v1/clones:create-by-asset-type";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Delete asset(s) previously copied between a source project and a target project.
   */
  platformDeleteAssets(body) {
    const url = "/v1/duplication/v1/clones:asset-delete";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Adds asset subscriptions to an existing clone.
   * Ideally, the clone_id parameter should have been named 'cloneId' to comply with our external
   * path arg naming conventions, but at this point it would be risky to change it as it would
   * impact the swagger file and any generated clients
   */
  platformUpdateClone(cloneId, body) {
    const url = `/v1/duplication/v1/clones/${cloneId}`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Use this endpoint to obtain a list of the currently created automations.  When calling
   * this endpoint you must at least specify two of the following: `source_project_uid`,
   * (`target_project_uid` or `target_email`) and `asset_type`, that is you can use:
   * * `source_project_uid` and `asset_type`:  To obtain the list of automations
   * originating from a given project that have a specific asset type.
   * * `target_project_uid` and `asset_type`:  To obtain the list of automations pointing
   * to the given project with a specific asset type.  Note that target_email is not yet
   * supported, so if used instead of `target_project_uid` it would raise a 400 error.
   * * `source_project_uid` and `target_project_uid`:  For listing the automations
   * originate and end in the given projects.
   * You can also specify all three of them.
   * For authentication, we validate that the requesting user is a valid user of at least
   * the source or target project.
   * This endpoint does not return the total number of results.
   */
  queryAutomations(params) {
    const url = {
      url: "/v1/automations",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Use this endpoint to traverse the directed graph of projects with automations between
   * themselves.  Given a project uid, it will return its neighbouring projects, that is
   * if the direction is `from` then we do the equivalent of querying the automations with
   * the `source_project_uid` passed by parameter, and so we return the projects
   * corresponding to the set of `target_project_uid`s of those automations.  When the
   * direction is `to` we do the opposite.
   * Note that the number of automations is an estimation which may be zero or temporarily
   * imprecise.
   * As for authentication, we require that the user making the request is a user of the
   * project passed by parameter.
   */
  queryNetwork(projectId, params) {
    const url = {
      url: `/v1/automations/neighbors/${projectId}`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint creates a bridge request.
   */
  requestBridge(body) {
    const url = "/v1/bridge-requests";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint resends a pending bridge request.
   */
  resendBridgeRequest(bridgeRequestId) {
    const url = `/v1/bridge-requests/${bridgeRequestId}/resend`;
    return this.raw.get(url);
  }
  /**
   * Resolve target asset uids based on the projects and source_asset uids, with
   * support for an optional seedId (uuid).
   * We don't use the sourceProjectId, but we might do it in the future, so please
   * be sure to send the correct uid in that field.
   */
  resolveTargetAssetIds(body) {
    const url = "/v1/resolve-target-asset-ids";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Creates a subscription between two objects
   * Returns:
   * 202: Subscription created and update request added to sqs queue
   * 409: Subscription already exists
   */
  subscriptionCreate(body) {
    const url = "/v1/subscriptions";
    return this.raw.post(url, jsonify(body));
  }
  subscriptionDelete(targetProjectUid, targetObjectUid) {
    const url = `/v1/subscriptions_/project_uid/${targetProjectUid}/object_uid/${targetObjectUid}`;
    return this.raw.delete(url);
  }
  systemInfo() {
    const url = "/v1/system/info";
    return this.raw.get(url);
  }
  templateCreate(body) {
    const url = "/v1/projects";
    return this.raw.post(url, jsonify(body));
  }
  templateDelete(targetProjectUid) {
    const url = `/v1/projects/${targetProjectUid}`;
    return this.raw.delete(url);
  }
  /**
   * Publish a template at the account level. Requires account admin level permissions.
   */
  templatePublish(templateId) {
    const url = `/v1/templates/${templateId}:publish`;
    return this.raw.post(url, {});
  }
  /**
   * Unpublish a template at the account level. Requires account admin level permissions.
   */
  templateUnpublish(templateId) {
    const url = `/v1/templates/${templateId}:unpublish`;
    return this.raw.post(url, {});
  }
  transmittalAccept(transmittalUid, body) {
    const url = `/v1/transmittals/${transmittalUid}/accept`;
    return this.raw.put(url, jsonify(body));
  }
  transmittalAcceptProgress(transmittalUid) {
    const url = `/v1/transmittals/${transmittalUid}/accept/progress`;
    return this.raw.get(url);
  }
  transmittalCreate(body) {
    const url = "/v1/transmittals";
    return this.raw.post(url, jsonify(body));
  }
  transmittalCreateMoreAssets(cloneUid, body) {
    const url = `/v1/transmittals/${cloneUid}`;
    return this.raw.post(url, jsonify(body));
  }
  transmittalDecline(transmittalUid, body) {
    const url = `/v1/transmittals/${transmittalUid}/decline`;
    return this.raw.put(url, jsonify(body));
  }
  transmittalDelete(transmittalUid) {
    const url = `/v1/transmittals/${transmittalUid}`;
    return this.raw.delete(url);
  }
  transmittalEdit(transmittalUid, body) {
    const url = `/v1/transmittals/${transmittalUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  transmittalSend(transmittalUid, body) {
    const url = `/v1/transmittals/${transmittalUid}/send`;
    return this.raw.put(url, jsonify(body));
  }
  transmittalSendProgress(transmittalUid) {
    const url = `/v1/transmittals/${transmittalUid}/send/progress`;
    return this.raw.get(url);
  }
  /**
   * this returns a list of recipients for all the transmittals
   */
  transmittalsRecipients(params) {
    const url = {
      url: "/v1/transmittals/recipients",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  transmittalWithdraw(transmittalUid) {
    const url = `/v1/transmittals/${transmittalUid}/send`;
    return this.raw.delete(url);
  }
  upsertSubscriptionsByAssetStatus(body) {
    const url = "/v1/subscriptions_/upsert_by_asset_status";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Validate usage of a template at the account level.
   */
  validateTemplate(params) {
    const url = {
      url: "/v1/template:validateUsage",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * This endpoint withdraws a bridge request.
   */
  withdrawBridgeRequest(bridgeRequestId) {
    const url = `/v1/bridge-requests/${bridgeRequestId}`;
    return this.raw.delete(url);
  }
  workspaceCreate(body) {
    const url = "/v1/workspaces";
    return this.raw.post(url, jsonify(body));
  }
}
